import React, { useCallback, useState } from 'react';
import { Modal, Radio, Typography, message } from 'antd-v5';
import FileDownload from 'js-file-download';
import { exportRolexInquiryDetail, exportRolexInquirySummary } from '../../../../api/vendors';
import { buildInquiryFilterQuery } from '../utils';

const { Text } = Typography;

const OPTIONS = [
  {
    value: 'summary',
    label: 'Summary report',
    description: 'A ZIP file containing one CSV per store with waiting counts by model for the filtered inquiries.',
  },
  {
    value: 'detail',
    label: 'Detailed report',
    description: 'A single CSV of the filtered inquiries with client, associate, story, and model details.',
  },
];

const formatRangeLabel = dateRange => {
  if (!dateRange?.[0] || !dateRange?.[1]) return 'All time';
  return `${dateRange[0].format('MMM D, YYYY')} – ${dateRange[1].format('MMM D, YYYY')}`;
};

// Filters other than the date range, which is shown on its own line
const FILTER_KEYS = ['clientSearch', 'status', 'associate', 'tags', 'collections', 'families', 'materials', 'stores'];

const countActiveFilters = filters =>
  FILTER_KEYS.filter(key => (Array.isArray(filters?.[key]) ? filters[key].length : !!filters?.[key])).length;

const ExportInquiriesModal = ({ open, onClose, filters }) => {
  const activeFilterCount = countActiveFilters(filters);
  const [reportType, setReportType] = useState('summary');
  const [loading, setLoading] = useState(false);

  const handleCancel = useCallback(() => {
    if (loading) return;
    onClose();
  }, [loading, onClose]);

  const handleExport = useCallback(async () => {
    setLoading(true);
    try {
      // Export what the table shows: same filters, no paging/sort
      const query = buildInquiryFilterQuery(filters);
      const fetcher = reportType === 'summary' ? exportRolexInquirySummary : exportRolexInquiryDetail;
      const { blob, filename } = await fetcher(query);
      FileDownload(blob, filename);
      message.success('Export downloaded');
      onClose();
    } catch (error) {
      console.error('Rolex inquiry export failed', error);
      message.error('There was an error generating your export. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, onClose, reportType]);

  return (
    <Modal
      title="Export Rolex inquiries"
      open={open}
      onOk={handleExport}
      onCancel={handleCancel}
      okText="Export"
      confirmLoading={loading}
      cancelButtonProps={{ disabled: loading }}
      maskClosable={!loading}
      closable={!loading}
      destroyOnClose
      width={480}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Text type="secondary">
          Date range: <Text strong>{formatRangeLabel(filters?.dateRange)}</Text>
        </Text>
        <Text type="secondary">
          Filters:{' '}
          <Text strong>
            {activeFilterCount ? `${activeFilterCount} applied` : 'None'}
          </Text>
        </Text>
        <Radio.Group
          value={reportType}
          onChange={e => setReportType(e.target.value)}
          disabled={loading}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {OPTIONS.map(option => (
            <Radio key={option.value} value={option.value} style={{ alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text strong>{option.label}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {option.description}
                </Text>
              </div>
            </Radio>
          ))}
        </Radio.Group>
      </div>
    </Modal>
  );
};

export default ExportInquiriesModal;
